import React, {useState} from 'react'
import {useAppState} from '../../state'
import {useTranslation} from 'react-i18next'
import {InputField} from '../tasks/CompletedTasks'
import {alarmTypeToTranslation} from './formatters'
import {Button, DatePicker, Input, Modal, Select, SelectOption} from '../../sharedComponents/components'
import {ColoredTogglerButton} from '../../Components/Atoms/ColoredTogglerButton'
import styled from 'styled-components'
import {colors} from '../../sharedComponents/colors'
import IsUserHealthInspector from '../../hooks/userIsHealthInspector'
import InputWithTags from '../tasks/GeneralTasks/InputWithTags'

export interface AlarmsFilters {
  alarmTypes: SelectOption[]
  completedBy: SelectOption[]
  startDate: Date
  endDate: Date
  media?: boolean
}

export interface AlarmsFilterViewProps {
  locale: string
  selectedFilter: AlarmsFilters
  setFilters: (key: string, value: any) => void
  resetFilters: () => void
  applyFilters: (overrides?: Partial<AlarmsFilters>) => void
  hasAppliedFilters: boolean
  onResolveAllTaskAlarms: (formData: {resolution: string; resolvedBy: string}) => Promise<any>
  onResolveAllApplianceAlarms: (formData: {resolution: string; resolvedBy: string}) => Promise<any>
  onResolveFilteredTaskAlarms: (formData: {resolution: string; resolvedBy: string}) => Promise<any>
  onResolveFilteredApplianceAlarms: (formData: {resolution: string; resolvedBy: string}) => Promise<any>
}

type BulkResolveModalType = 'all-task' | 'all-appliance' | 'filtered-task' | 'filtered-appliance' | null

export const AlarmsFilterView = ({
  resetFilters,
  applyFilters,
  selectedFilter,
  setFilters,
  locale,
  hasAppliedFilters,
  onResolveAllTaskAlarms,
  onResolveAllApplianceAlarms,
  onResolveFilteredTaskAlarms,
  onResolveFilteredApplianceAlarms
}: AlarmsFilterViewProps) => {
  const {state} = useAppState()
  const {t} = useTranslation(['alarms', 'tasks'])
  const {filters} = state.v1.alarms
  const {isHealthInspector} = IsUserHealthInspector()
  const siteId = state.site?.id
  const disableBulkResolve =
    isHealthInspector ||
    (!state.me?.accessRights?.superuser && !(siteId && state.me?.accessRights?.write?.sites?.includes(siteId)))
  const canBulkResolve = !disableBulkResolve
  const actorSuggestions = state.site?.actorSuggestions ?? []
  const hasActorSuggestions = actorSuggestions.length > 0
  const [bulkResolveType, setBulkResolveType] = useState<BulkResolveModalType>(null)
  const [formData, setFormData] = useState({resolution: '', resolvedBy: ''})
  const [submitting, setSubmitting] = useState(false)

  const resetBulkResolveModal = () => {
    setBulkResolveType(null)
    setFormData({resolution: '', resolvedBy: ''})
    setSubmitting(false)
  }

  const handleResolvedByChange = (resolvedBy: string) => {
    setFormData(prev => ({...prev, resolvedBy}))
  }

  const onBulkResolveSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (
      (formData.resolution ?? '').trim().length === 0 ||
      (formData.resolvedBy ?? '').trim().length === 0 ||
      submitting ||
      bulkResolveType === null
    ) {
      return
    }

    setSubmitting(true)
    try {
      if (bulkResolveType === 'all-task') {
        await onResolveAllTaskAlarms(formData)
      } else if (bulkResolveType === 'all-appliance') {
        await onResolveAllApplianceAlarms(formData)
      } else if (bulkResolveType === 'filtered-task') {
        await onResolveFilteredTaskAlarms(formData)
      } else {
        await onResolveFilteredApplianceAlarms(formData)
      }
      resetBulkResolveModal()
    } finally {
      setSubmitting(false)
    }
  }

  const bulkResolveTitle = {
    'all-task': t('alarms:buttons.resolveAllTaskAlarms', 'Resolve all task alarms'),
    'all-appliance': t('alarms:buttons.resolveAllApplianceAlarms', 'Resolve all appliance alarms'),
    'filtered-task': t('alarms:buttons.resolveFilteredTaskAlarms', 'Resolve filtered task alarms'),
    'filtered-appliance': t('alarms:buttons.resolveFilteredApplianceAlarms', 'Resolve filtered appliance alarms')
  }[bulkResolveType ?? 'all-task']

  const hasActiveTaskAlarms = state.v1.alarms.taskAlarms.some(alarm => alarm.status === 'active')
  const hasActiveApplianceAlarms = state.v1.alarms.applianceAlarms.some(alarm => alarm.status === 'active')

  const bulkResolveDescription = t(
    'alarms:resolve.Description',
    'You are about to resolve all selected alarms. The resolution details entered below will be applied to all selected alarms.'
  )

  return (
    <>
      <InputField>
        <label htmlFor="triggeredTime">{t('alarms:labels.filter.triggeredTimeHeading', 'Triggered time')}</label>
        <DatePicker
          id="triggeredTime"
          onChange={setFilters}
          startDate={selectedFilter.startDate}
          endDate={selectedFilter.endDate}
          locale={locale}
        />
      </InputField>
      <InputField>
        <label htmlFor="alarmTypes">{t('alarms:labels.filter.alarmTypeHeading', 'Alarm type')}</label>
        <Select
          id="alarmTypes"
          onChange={value => setFilters('alarmTypes', value)}
          value={selectedFilter.alarmTypes}
          placeholder={t('tasks:labels.selectFilters', 'Select...')}
          options={filters.alarmTypes.map(v => ({value: v, label: alarmTypeToTranslation(t, v)}))}
          multiple
        />
      </InputField>
      {/* <InputField>
        <label htmlFor="completedBy">{t('tasks:labels.columnNameCompletedBy', 'Completed By')}</label>
        <Select
          id="completedBy"
          onChange={(value: any) => setFilters('completedBy', value)}
          value={selectedFilter.completedBy}
          placeholder={t('tasks:labels.selectFilters', 'Select...')}
          options={filters.completedBy.map(value => ({value: value.id, label: value.name}))}
          multiple
        />
      </InputField> */}
      <InputField>
        <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>{t('tasks:labels.media', 'Image')}</label>
        <ColoredTogglerButton
          checked={!!selectedFilter.media}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const newState = !!e.target.checked
            setFilters('media', newState)
          }}
        />
      </InputField>
      <div style={{display: 'flex', justifyContent: 'space-between'}}>
        <Button buttonStyle="secondary" buttonSize="small" onClick={resetFilters}>
          {t('tasks:buttons.resetFilters', 'Reset')}
        </Button>
        <Button buttonStyle="primary" buttonSize="small" onClick={() => applyFilters()}>
          {t('tasks:buttons.applyFilters', 'Apply filters')}
        </Button>
      </div>
      {canBulkResolve && (
        <BulkResolveButtonsContainer>
          <Button
            buttonStyle="secondary"
            buttonSize="small"
            onClick={() => setBulkResolveType('all-task')}
            disabled={!hasActiveTaskAlarms}
          >
            {t('alarms:buttons.resolveAllTaskAlarms', 'Resolve all task alarms')}
          </Button>
          <Button
            buttonStyle="secondary"
            buttonSize="small"
            onClick={() => setBulkResolveType('filtered-task')}
            disabled={!hasAppliedFilters || !hasActiveTaskAlarms}
          >
            {t('alarms:buttons.resolveFilteredTaskAlarms', 'Resolve filtered task alarms')}
          </Button>
          <BulkResolveDivider />
          <Button
            buttonStyle="secondary"
            buttonSize="small"
            onClick={() => setBulkResolveType('all-appliance')}
            disabled={!hasActiveApplianceAlarms}
          >
            {t('alarms:buttons.resolveAllApplianceAlarms', 'Resolve all appliance alarms')}
          </Button>
          <Button
            buttonStyle="secondary"
            buttonSize="small"
            onClick={() => setBulkResolveType('filtered-appliance')}
            disabled={!hasAppliedFilters || !hasActiveApplianceAlarms}
          >
            {t('alarms:buttons.resolveFilteredApplianceAlarms', 'Resolve filtered appliance alarms')}
          </Button>
        </BulkResolveButtonsContainer>
      )}
      <Modal
        isOpen={bulkResolveType !== null}
        onClose={submitting ? undefined : resetBulkResolveModal}
        title={bulkResolveTitle}
        minHeight="320px"
        maxWidth="578px"
        headerHeight="64px"
        headerBackgroundColor={colors.system.red}
        headerLeftItem={
          <Button buttonStyle="secondary" buttonSize="small" onClick={resetBulkResolveModal} disabled={submitting}>
            {t('common:buttons.cancel', 'Cancel')}
          </Button>
        }
        headerRightItem={
          <Button
            buttonStyle="primary"
            buttonSize="small"
            onClick={onBulkResolveSubmit}
            disabled={
              (formData.resolution ?? '').trim().length === 0 ||
              (formData.resolvedBy ?? '').trim().length === 0 ||
              submitting ||
              disableBulkResolve
            }
          >
            {t('common:buttons.resolve', 'Resolve')}
          </Button>
        }
      >
        <BulkResolveModalContent>
          <BulkResolveDescription>{bulkResolveDescription}</BulkResolveDescription>
          {hasActorSuggestions ? (
            <InputWithTags
              label={t('appliances:labels.resolvedBy', 'Resolved By')}
              placeholder={t('appliances:placeholders.resolvedBy', 'Enter name')}
              savedSuggestions={formData.resolvedBy}
              defaultSuggestions={actorSuggestions}
              onChange={(_key, selected) => handleResolvedByChange(selected[0] ?? '')}
              disabled={submitting || disableBulkResolve}
              required
            />
          ) : (
            <Input
              id="bulkResolveAlarmModalFormResolved"
              name="bulkResolveAlarmModalFormResolved"
              label={t('appliances:labels.resolvedBy', 'Resolved By')}
              placeholder={t('appliances:placeholders.resolvedBy', 'Enter name')}
              onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleResolvedByChange(event.target.value)
              }
              value={formData.resolvedBy}
              disabled={submitting || disableBulkResolve}
              required
            />
          )}
          <Input
            id="bulkResolveAlarmModalFormReason"
            name="bulkResolveAlarmModalFormReason"
            label={t('appliances:labels.description', 'Description')}
            placeholder={t('appliances:placeholders.resolution', 'Enter alarm reason & resolution')}
            multiline
            rows={6}
            onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              setFormData(prev => ({...prev, resolution: event.target.value}))
            }
            value={formData.resolution}
            disabled={submitting || disableBulkResolve}
            required
          />
        </BulkResolveModalContent>
      </Modal>
    </>
  )
}

const BulkResolveButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 3rem;
`

const BulkResolveDivider = styled.hr`
  width: 100%;
  margin: 0.25rem 0;
  border: 0;
  border-top: 1px solid ${colors.system.grey_5};
`

const BulkResolveModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
`

const BulkResolveDescription = styled.p`
  margin: 0;
  color: ${colors.system.red};
`
